import React from 'react'
import styles from './MicrositeFeaturesSectionForm.module.css'
import TextInput from '../../../components/TextInput/TextInput'
import MicrositeRepeatableRows from '../../../components/MicrositeRepeatableRows/MicrositeRepeatableRows'

// ──────────────────────────────────────────────────────────────
// Default skeleton matching the real production content shape.
// ──────────────────────────────────────────────────────────────
const DEFAULT_CONTENT = {
    title: '',
    subtitle: '',
    description: '',
    features: []
}

const EMPTY_FEATURE = { title: '', description: '' }

// Backfills any missing top-level key so older or partially-saved
// content still renders without crashing.
const withDefaults = (content) => ({
    title: content?.title ?? DEFAULT_CONTENT.title,
    subtitle: content?.subtitle ?? DEFAULT_CONTENT.subtitle,
    description: content?.description ?? DEFAULT_CONTENT.description,
    features: content?.features || []
})

/**
 * MicrositeFeaturesSectionForm
 * Content editor for the landing page "Features" section, matching
 * the real production content shape: title/subtitle/description
 * plus a repeatable list of feature highlights, each with just a
 * title and description (no icon field — that doesn't exist in
 * the real per-feature shape and was dropped here).
 *
 * Pure presentational sub-form — receives `content` and `onChange`
 * from MicrositeLandingSection, which owns loading/saving/versioning.
 * No DB or API changes required: osm_store_config_landing.content is
 * a generic JSON column already; this form just sends the matching
 * object into the same micrositeUpdateLandingSection contract.
 */
const MicrositeFeaturesSectionForm = ({ content, onChange }) => {
    const safe = withDefaults(content)

    const update = (field, value) => onChange?.({ ...safe, [field]: value })

    return (
        <section className={styles.fieldGroup}>
            <h2 className={styles.groupTitle}>Features</h2>
            <p className={styles.groupDesc}>Highlight what makes your salon stand out.</p>

            <TextInput
                label="Title"
                value={safe.title}
                onChange={(v) => update('title', v)}
                placeholder="e.g. Why Choose Us?"
                maxLength={80}
                showCharCount
            />
            <TextInput
                label="Subtitle"
                value={safe.subtitle}
                onChange={(v) => update('subtitle', v)}
                placeholder="e.g. Our Excellence"
                maxLength={60}
                showCharCount
            />
            <TextInput
                label="Description"
                value={safe.description}
                onChange={(v) => update('description', v)}
                placeholder="e.g. The numbers speak for themselves."
                maxLength={220}
                showCharCount
            />

            <label className={styles.rowsLabel}>Feature highlights</label>
            <MicrositeRepeatableRows
                items={safe.features}
                onChange={(v) => update('features', v)}
                emptyItem={{ ...EMPTY_FEATURE }}
                addLabel="Add feature"
                renderSummary={(item) => item.title || 'New feature'}
                renderItem={(item, idx, updateItem) => (
                    <div className={styles.featureFields}>
                        <TextInput
                            label="Title"
                            value={item.title}
                            onChange={(v) => updateItem('title', v)}
                            placeholder="e.g. Expert Staff"
                        />
                        <TextInput
                            label="Description"
                            value={item.description}
                            onChange={(v) => updateItem('description', v)}
                            placeholder="e.g. Our team consists of highly trained professionals."
                        />
                    </div>
                )}
            />
        </section>
    )
}

export default MicrositeFeaturesSectionForm